import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { login } from '../store/authSlice';
import axiosInstance from '../axiosInstance';
import { setAuthToken } from '../auth/authUtils';
import { useAuth } from './useAuth';
import { useSecurity } from './useSecurity';
import { useDeviceFingerprint } from './useDeviceFingerprint';

/**
 * Shared login form hook — all login logic in one place.
 * Every login page variant uses this hook for identical behaviour.
 */
const useLoginForm = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Handle social auth callback (token in URL params after OAuth redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const socialAuth = params.get('social_auth');
    const token = params.get('token');
    const socialError = params.get('error');

    if (socialAuth === 'success' && token) {
      setAuthToken(token, true);
      const expiresAt = params.get('expires_at');
      if (expiresAt) localStorage.setItem('token_expires_at', expiresAt);

      // Fetch user profile with the token
      axiosInstance.get('/user', {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        dispatch(login({ user: res.data, token }));
        // Check for promo returnTo in localStorage (survives OAuth redirect)
        const promoReturn = localStorage.getItem('promo_returnTo');
        if (promoReturn) {
          localStorage.removeItem('promo_returnTo');
          navigate(promoReturn, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      }).catch(() => {
        dispatch(login({ user: {}, token }));
        const promoReturn = localStorage.getItem('promo_returnTo');
        if (promoReturn) {
          localStorage.removeItem('promo_returnTo');
          navigate(promoReturn, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      });
    } else if (socialAuth === 'error' && socialError) {
      setError(socialError);
    }
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [retryAfter, setRetryAfter] = useState(null);

  const [socialProviders, setSocialProviders] = useState([]);

  const { validateInput, sanitizeInput } = useAuth();
  const { checkSecurityStatus, recordSecurityEvent } = useSecurity();
  const deviceFingerprint = useDeviceFingerprint();

  // Fetch available social providers
  useEffect(() => {
    axiosInstance.get('/auth/social/providers').then(res => {
      if (res.data?.success) setSocialProviders(res.data.data || []);
    }).catch(() => {});
  }, []);

  const handleSocialLogin = (provider) => {
    // Save returnTo before OAuth redirect (query params are lost during redirect)
    const urlParams = new URLSearchParams(location.search);
    const returnTo = urlParams.get('returnTo');
    if (returnTo) {
      localStorage.setItem('promo_returnTo', returnTo);
    }
    const apiBase = axiosInstance.defaults.baseURL || '';
    window.location.href = `${apiBase}/auth/social/${provider}/redirect`;
  };

  // Cookie notice
  const [showCookieNotice, setShowCookieNotice] = useState(() => !localStorage.getItem('cookiesAccepted'));

  const handleAcceptAll = () => {
    localStorage.setItem('cookiesAccepted', 'all');
    localStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
    setShowCookieNotice(false);
  };

  const handleOnlyNecessary = () => {
    localStorage.setItem('cookiesAccepted', 'necessary');
    localStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
    setShowCookieNotice(false);
  };

  const validateForm = useCallback(() => {
    const errors = {};
    if (!email.trim()) {
      errors.email = 'Email address is required';
    } else if (!validateInput.email(email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password, validateInput]);

  const performSecurityCheck = useCallback(async () => {
    try {
      const securityStatus = await checkSecurityStatus();
      if (!securityStatus.safe) {
        setError(securityStatus.message);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  }, [checkSecurityStatus]);

  const handleEmailChange = (value) => {
    setEmail(value);
    if (error || validationErrors.email) {
      setError('');
      setValidationErrors(prev => ({ ...prev, email: null }));
    }
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    if (error || validationErrors.password) {
      setError('');
      setValidationErrors(prev => ({ ...prev, password: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationErrors({});
    setRetryAfter(null);

    if (!validateForm()) return;
    if (!(await performSecurityCheck())) return;

    setLoading(true);

    try {
      const sanitizedEmail = sanitizeInput.email(email.trim());
      const sanitizedPassword = sanitizeInput.password(password);

      await recordSecurityEvent('login_attempt', {
        email: sanitizedEmail,
        device_fingerprint: deviceFingerprint,
        timestamp: new Date().toISOString(),
      });

      const response = await axiosInstance.post('/login', {
        email: sanitizedEmail,
        password: sanitizedPassword,
        remember_me: rememberMe,
        device_fingerprint: deviceFingerprint,
      }, {
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Device-Fingerprint': deviceFingerprint,
        },
      });

      if (response.status === 200 && response.data.success) {
        const { user, token, expires_at } = response.data.data;

        // Store token + remember-me preference via centralized util
        setAuthToken(token, rememberMe);
        if (expires_at) {
          localStorage.setItem('token_expires_at', expires_at);
        }

        dispatch(login({ user, token }));
        setEmail('');
        setPassword('');
        setRememberMe(false);

        await recordSecurityEvent('login_success', {
          user_id: user.id,
          email: user.email,
          device_fingerprint: deviceFingerprint,
        });

        // Check for returnTo in URL query params (from promo pages)
        const urlParams = new URLSearchParams(location.search);
        const returnTo = urlParams.get('returnTo');
        // Also check localStorage (from promo pages before social auth)
        const promoReturn = localStorage.getItem('promo_returnTo');
        if (promoReturn) localStorage.removeItem('promo_returnTo');

        const from = returnTo || promoReturn || location.state?.from?.pathname || '/home';
        navigate(from, { replace: true });
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (err) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
        setRetryAfter(err.response.data.retry_after || 300);
      } else if (err.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Your account has been deactivated. Please contact support.';
      } else if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        if (errors) {
          setValidationErrors(errors);
          errorMessage = 'Please correct the errors below.';
        } else {
          errorMessage = err.response.data.message || 'Validation failed.';
        }
      } else if (err.response?.status === 400) {
        errorMessage = err.response.data.message || 'Invalid request. Please try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      await recordSecurityEvent('login_failed', {
        email,
        error: errorMessage,
        device_fingerprint: deviceFingerprint,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    password,
    showPassword,
    rememberMe,
    loading,
    error,
    validationErrors,
    retryAfter,
    showCookieNotice,
    setShowPassword,
    setRememberMe,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit,
    handleAcceptAll,
    handleOnlyNecessary,
    socialProviders,
    handleSocialLogin,
  };
};

export default useLoginForm;
